/**
 * Device and Browser Fingerprinting Utility
 * Collects device information for order security and fraud prevention
 */

/**
 * Detects the device type based on user agent and screen properties
 * @returns {string} - 'mobile', 'tablet', or 'desktop'
 */
function getDeviceType() {
    const userAgent = navigator.userAgent || ''
    const screenWidth = window.screen?.width || 0

    // Check for mobile patterns
    if (
        /Mobi|Android|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
            userAgent
        )
    ) {
        // Distinguish between mobile and tablet
        if (/iPad|Android(?!.*Mobile)/i.test(userAgent) || screenWidth >= 768) {
            return 'tablet'
        }
        return 'mobile'
    }

    return 'desktop'
}

/**
 * Parses user agent to extract browser information
 * @returns {Object} - Browser name and version
 */
function getBrowserInfo() {
    const userAgent = navigator.userAgent || ''

    // Browser detection patterns
    const browsers = [
        { name: 'Chrome', pattern: /Chrome\/(\d+)/ },
        { name: 'Firefox', pattern: /Firefox\/(\d+)/ },
        { name: 'Safari', pattern: /Version\/(\d+).*Safari/ },
        { name: 'Edge', pattern: /Edg\/(\d+)/ },
        { name: 'Opera', pattern: /OPR\/(\d+)/ },
        { name: 'Internet Explorer', pattern: /MSIE (\d+)|Trident.*rv:(\d+)/ },
    ]

    for (const browser of browsers) {
        const match = userAgent.match(browser.pattern)
        if (match) {
            return {
                name: browser.name,
                version: match[1] || match[2] || 'unknown',
            }
        }
    }

    return { name: 'unknown', version: 'unknown' }
}

/**
 * Parses user agent to extract OS information
 * @returns {Object} - OS name and version
 */
function getOSInfo() {
    const userAgent = navigator.userAgent || ''

    // OS detection patterns
    if (/Windows NT (\d+\.\d+)/.test(userAgent)) {
        const match = userAgent.match(/Windows NT (\d+\.\d+)/)
        return { name: 'Windows', version: match[1] }
    }

    if (/Mac OS X (\d+[._]\d+[._]?\d*)/.test(userAgent)) {
        const match = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/)
        return { name: 'macOS', version: match[1].replace(/_/g, '.') }
    }

    if (/Android (\d+\.\d+)/.test(userAgent)) {
        const match = userAgent.match(/Android (\d+\.\d+)/)
        return { name: 'Android', version: match[1] }
    }

    if (/iPhone OS (\d+[._]\d+[._]?\d*)/.test(userAgent)) {
        const match = userAgent.match(/iPhone OS (\d+[._]\d+[._]?\d*)/)
        return { name: 'iOS', version: match[1].replace(/_/g, '.') }
    }

    if (/Linux/.test(userAgent)) {
        return { name: 'Linux', version: 'unknown' }
    }

    return { name: 'unknown', version: 'unknown' }
}

/**
 * Gets screen resolution
 * @returns {string} - Screen resolution in format "WIDTHxHEIGHT"
 */
function getScreenResolution() {
    if (window.screen) {
        return `${window.screen.width}x${window.screen.height}`
    }
    return 'unknown'
}

/**
 * Gets browser language
 * @returns {string} - Language code
 */
function getLanguage() {
    return navigator.language || navigator.userLanguage || 'unknown'
}

/**
 * Gets timezone information
 * @returns {string} - Timezone identifier
 */
function getTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown'
    } catch (error) {
        return 'unknown'
    }
}

/**
 * Generates a device ID based on available browser characteristics
 * Note: This is a simple fingerprint, not cryptographically secure
 * @returns {string} - Device ID
 */
function generateDeviceId() {
    const components = [
        navigator.userAgent || '',
        getScreenResolution(),
        navigator.language || '',
        navigator.hardwareConcurrency || '',
        navigator.deviceMemory || '',
        getTimezone(),
        navigator.maxTouchPoints || 0,
        navigator.cookieEnabled ? 'cookies-enabled' : 'cookies-disabled',
        navigator.doNotTrack || 'not-set',
        navigator.onLine ? 'online' : 'offline',
        window.screen?.colorDepth || '',
        window.screen?.pixelDepth || '',
        window.devicePixelRatio || '',
    ]

    // Simple hash function for generating device ID
    let hash = 0
    const str = components.join('|')
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(16)
}

/**
 * Attempts to get location information from apip.cc service
 * @returns {Promise<Object>} - Location information
 */
async function getLocationInfo() {
    try {
        // Fetch location data from apip.cc service
        const response = await fetch('https://apip.cc/json', {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            // Set a reasonable timeout
            signal: AbortSignal.timeout(5000), // 5 second timeout
        })

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        // Check if the response indicates success
        if (data.status !== 'success') {
            throw new Error('Location service returned error status')
        }

        return {
            ip: data.ip || null,
            country: data.CountryName || null,
            city: data.City || null,
            region: data.RegionName || null,
        }
    } catch (error) {
        console.warn('Failed to fetch location information:', error.message)
        // Return null values if service fails - fingerprinting will still proceed
        return {
            ip: null,
            country: null,
            city: null,
            region: null,
        }
    }
}

/**
 * Collects comprehensive device and browser fingerprint data
 * @returns {Promise<Object>} - Complete fingerprint object
 */
export async function collectFingerprint() {
    const browserInfo = getBrowserInfo()
    const osInfo = getOSInfo()
    const locationInfo = await getLocationInfo()

    return {
        ip: locationInfo.ip, // Fetched from apip.cc service
        userAgent: navigator.userAgent || 'unknown',
        country: locationInfo.country, // Fetched from apip.cc service
        city: locationInfo.city, // Fetched from apip.cc service
        region: locationInfo.region, // Fetched from apip.cc service
        timezone: getTimezone(),
        deviceId: generateDeviceId(),
        deviceType: getDeviceType(),
        browser: browserInfo.name,
        browserVersion: browserInfo.version,
        os: osInfo.name,
        osVersion: osInfo.version,
        screenResolution: getScreenResolution(),
        language: getLanguage(),
        timestamp: new Date(),
    }
}

/**
 * Simplified version for cases where you need just basic fingerprint data
 * @returns {Object} - Basic fingerprint object
 */
export function getBasicFingerprint() {
    const browserInfo = getBrowserInfo()
    const osInfo = getOSInfo()

    return {
        userAgent: navigator.userAgent || 'unknown',
        deviceId: generateDeviceId(),
        deviceType: getDeviceType(),
        browser: browserInfo.name,
        browserVersion: browserInfo.version,
        os: osInfo.name,
        osVersion: osInfo.version,
        screenResolution: getScreenResolution(),
        language: getLanguage(),
        timezone: getTimezone(),
        timestamp: new Date(),
    }
}
